import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useInvoices } from '@/hooks/use-billing'
import { useMembersList } from '@/hooks/use-members'
import { useResolveC2BTransaction } from '@/hooks/use-payments'
import { useUtilityTypes } from '@/hooks/use-utilities'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/utils'
import type { RecordPaymentResult, UnmatchedC2BTransaction } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

type ApplyMode = 'everything' | 'invoice' | 'utilityType'

export function AssignC2BPaymentModal({
  transaction,
  onClose,
}: {
  transaction: UnmatchedC2BTransaction
  onClose: () => void
}) {
  const resolve = useResolveC2BTransaction()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [memberId, setMemberId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RecordPaymentResult | null>(null)

  const [applyMode, setApplyMode] = useState<ApplyMode>('everything')
  const [invoiceId, setInvoiceId] = useState('')
  const [utilityTypeId, setUtilityTypeId] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data } = useMembersList({ page: 1, limit: 20, search: debouncedSearch || undefined })
  const members = data?.data ?? []

  const { data: invoicesData } = useInvoices({
    memberId,
    excludeInvoiceType: 'credit_note',
    limit: 100,
  })
  const openInvoices = (invoicesData?.data ?? []).filter(
    (inv) => inv.status !== 'paid' && inv.status !== 'voided',
  )
  const { data: utilityTypesData } = useUtilityTypes(applyMode === 'utilityType')
  const utilityTypes = utilityTypesData?.data ?? []

  function handleSelectMember(id: string) {
    setMemberId(id)
    setApplyMode('everything')
    setInvoiceId('')
    setUtilityTypeId('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!memberId) {
      setError('Select a member')
      return
    }
    if (applyMode === 'invoice' && !invoiceId) {
      setError('Choose which invoice to apply this to')
      return
    }
    if (applyMode === 'utilityType' && !utilityTypeId) {
      setError('Choose which utility type to apply this to')
      return
    }
    setError(null)
    try {
      const { data } = await resolve.mutateAsync({
        transactionId: transaction.id,
        memberId,
        target:
          applyMode === 'invoice'
            ? { invoiceId }
            : applyMode === 'utilityType'
              ? { utilityTypeId }
              : undefined,
      })
      setResult(data)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to assign payment'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="assign-c2b-payment-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              {formatKES(transaction.amount)} · {transaction.billRefNumber || 'no account number'}
            </p>
            <h2 id="assign-c2b-payment-title" className="font-serif text-xl text-ink">
              Assign payment
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      {result ? (
        <>
          <ModalBody className="space-y-3">
            {result.applications.length > 0 && (
              <>
                <p className="font-sans text-sm text-ink">
                  Applied to {result.applications.length} invoice(s)
                  {applyMode === 'everything' ? ', oldest first' : ''}:
                </p>
                <ul className="space-y-1.5">
                  {result.applications.map((a) => (
                    <li
                      key={a.invoiceId}
                      className="flex items-center justify-between rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-sm"
                    >
                      <span className="text-ink">{a.invoiceNumber}</span>
                      <span className="text-muted-foreground">
                        {formatKES(a.amountApplied)} — now {a.invoiceStatus}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {result.creditAmount > 0 && (
              <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-xs text-muted-foreground">
                {result.applications.length > 0
                  ? `${formatKES(result.creditAmount)} exceeded everything this member owed — recorded as a credit against future invoices.`
                  : `Nothing was outstanding to apply this to — the full ${formatKES(result.creditAmount)} was recorded as a credit against future invoices${applyMode === 'utilityType' ? ' for this utility type' : ''}.`}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <ModalBody className="space-y-4">
            <p className="font-sans text-sm text-muted-foreground">
              This payment's account number didn't match any plot, invoice, or utility type. Pick
              which member it belongs to, and — if the account number they typed suggests they meant
              a specific bill — where to apply it.
            </p>
            <Input
              label="Search members"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, phone, email…"
            />
            <div className="max-h-48 overflow-y-auto rounded border border-border-2">
              {members.length === 0 ? (
                <p className="px-3 py-4 font-sans text-sm text-muted-foreground">
                  No members found
                </p>
              ) : (
                members.map((m) => (
                  <label
                    key={m.id}
                    className="flex cursor-pointer items-start gap-3 border-b border-border-2 px-3 py-2 last:border-0 hover:bg-cream-2/50"
                  >
                    <input
                      type="radio"
                      name="member"
                      checked={memberId === m.id}
                      onChange={() => handleSelectMember(m.id)}
                      className="mt-1"
                    />
                    <span className="min-w-0 font-sans text-sm">
                      <span className="font-medium text-ink">{m.name}</span>
                      <span className="block text-xs text-muted-foreground">{m.phone}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
            {memberId && (
              <>
                <div className="space-y-1.5">
                  <label className="font-sans text-sm font-medium text-ink">Apply to</label>
                  <select
                    className={SELECT_CLS}
                    value={applyMode}
                    onChange={(e) => setApplyMode(e.target.value as ApplyMode)}
                  >
                    <option value="everything">Whole balance (oldest invoice first)</option>
                    <option value="invoice">One invoice</option>
                    <option value="utilityType">One utility type</option>
                  </select>
                </div>
                {applyMode === 'invoice' && (
                  <div className="space-y-1.5">
                    <label className="font-sans text-sm font-medium text-ink">Invoice</label>
                    <select
                      className={SELECT_CLS}
                      value={invoiceId}
                      onChange={(e) => setInvoiceId(e.target.value)}
                    >
                      <option value="">Select an invoice…</option>
                      {openInvoices.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {inv.invoiceNumber} — {formatKES(inv.balance)} due
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {applyMode === 'utilityType' && (
                  <div className="space-y-1.5">
                    <label className="font-sans text-sm font-medium text-ink">Utility type</label>
                    <select
                      className={SELECT_CLS}
                      value={utilityTypeId}
                      onChange={(e) => setUtilityTypeId(e.target.value)}
                    >
                      <option value="">Select a utility type…</option>
                      {utilityTypes.map((ut) => (
                        <option key={ut.id} value={ut.id}>
                          {ut.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
            {error && (
              <p className="font-sans text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={resolve.isPending}>
              Assign
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  )
}
